import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, Upload, message, Tag, Space, Row, Col, Card, Typography, Popconfirm, Image, Progress, Drawer, Descriptions, Tooltip, DatePicker, Badge, List, Statistic, AutoComplete, Checkbox, Dropdown } from 'antd';
import { PlusOutlined, UploadOutlined, SearchOutlined, PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined, DeleteOutlined, EditOutlined, CloudUploadOutlined, YoutubeOutlined, FileProtectOutlined, InboxOutlined, CheckCircleFilled, CloseCircleFilled, CustomerServiceOutlined, AppstoreOutlined, SoundOutlined, CloudServerOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';
import { hasPerm } from '../utils/perms';
import { useAudioPlayer } from '../components/AudioPlayerContext';

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function Songs({ user }) {
  const userRole = user?.role || 'viewer';
  const authToken = localStorage.getItem('token');
  const location = useLocation();
  // Audio download: privileged roles, or any user granted the 'audio_download' permission
  const canDownloadAudio = ['admin', 'manager'].includes(userRole) || hasPerm(user, 'audio_download');
  // Read URL query params for pre-filtering from Dashboard clicks
  const initFilters = () => {
    const p = new URLSearchParams(location.search);
    const distStatus = p.get('distribution_status') || '';
    return {
      search: '', company_id: '', status: distStatus,
      iprs: p.get('iprs_status') || '', agreement: '', from: '', to: '', page: 1,
      no_poster: p.get('no_poster') || '', studio: '', category: p.get('category') || '',
      audio: p.get('audio') || ''
    };
  };
  const [songs, setSongs] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: 0, distributed: 0, pending: 0 });
  const [agreementCounts, setAgreementCounts] = useState({ total: 0, not_made: 0, made_pending: 0, uploaded: 0 });
  const [audioCounts, setAudioCounts] = useState({ total: 0, uploaded: 0, not_uploaded: 0 });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [bulkAudioModal, setBulkAudioModal] = useState(false);
  const [bulkPosterModal, setBulkPosterModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState({ title: '', file: '', loaded: 0, total: 0, speed: 0 });
  const uploadStartRef = useRef(0);
  const [audioItems, setAudioItems] = useState([]);
  const [bulkAudioFiles, setBulkAudioFiles] = useState([]);
  const [editingSong, setEditingSong] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState(initFilters());
  const [playingSong, setPlayingSong] = useState(null);
  const [bulkAudioDate, setBulkAudioDate] = useState(dayjs());
  const [bulkPosterDate, setBulkPosterDate] = useState(dayjs());
  const [audioDateRange, setAudioDateRange] = useState(null);
  const [detailSong, setDetailSong] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestModal, setSuggestModal] = useState(false);
  const [dupModal, setDupModal] = useState(false);
  const [dupItems, setDupItems] = useState([]);
  const [dupPendingUpload, setDupPendingUpload] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editTitleVal, setEditTitleVal] = useState('');
  const [mergeGroups, setMergeGroups] = useState([]);
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [mergingGroup, setMergingGroup] = useState(null);
  const [studioOptions, setStudioOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uncategorized, setUncategorized] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [newCatModal, setNewCatModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const globalPlayer = useAudioPlayer();
  const [form] = Form.useForm();
  const [bulkForm] = Form.useForm();

  useEffect(() => { fetchCompanies(); if (['admin', 'super_admin', 'manager'].includes(userRole)) { fetchSuggestions(); fetchMergeSuggestions(); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchSongs(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  useEffect(() => { setFilters(initFilters()); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSongs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/songs', { params: filters });
      setSongs(res.data.songs);
      setTotal(res.data.total);
      if (res.data.counts) setCounts(res.data.counts);
      if (res.data.agreement_counts) setAgreementCounts(res.data.agreement_counts);
      if (res.data.audio_counts) setAudioCounts(res.data.audio_counts);
      setStudioOptions(res.data.studios || []);
      setCategories(res.data.categories || []);
      setUncategorized(res.data.uncategorized || 0);
      setAllTotal(res.data.all_total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async (values) => {
    try {
      if (editingSong) {
        await api.put(`/songs/${editingSong.id}`, values);
        message.success('Song updated!');
      } else {
        await api.post('/songs', values);
        message.success('Song created!');
      }
      setModalVisible(false);
      setEditingSong(null);
      form.resetFields();
      fetchSongs();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/songs/${id}`);
      message.success('Song deleted');
      setSelectedRowKeys(keys => keys.filter(k => k !== id));
      fetchSongs();
    } catch (err) { message.error('Delete failed'); }
  };

  const handleSongTitleEdit = async (id) => {
    if (!editTitleVal.trim()) { message.warning('Title cannot be empty'); return; }
    try {
      await api.put(`/songs/${id}/title`, { title: editTitleVal.trim() });
      message.success('Title updated');
      setEditingTitleId(null);
      fetchSongs();
    } catch (err) { message.error('Failed to update title'); }
  };

  const categoryOptions = categories.map(c => ({ value: c.name, label: c.name }));

  const addCategory = async () => {
    const name = newCatName.trim();
    if (!name) { message.warning('Section ka naam likho'); return; }
    try {
      await api.post('/songs/categories', { name });
      message.success(`Section "${name}" ban gaya`);
      setNewCatModal(false); setNewCatName('');
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const setSongCategory = async (song, category) => {
    try {
      await api.put(`/songs/${song.id}/category`, { category: category || '' });
      message.success(category ? `Moved to ${category}` : 'Section hataya');
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const bulkMoveToCategory = async (category) => {
    if (!category) return;
    try {
      const res = await api.post('/songs/bulk-category', { ids: selectedRowKeys, category });
      message.success(res.data.message || 'Moved');
      setBulkCategoryValue('');
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const setAgreementMade = async (song, made) => {
    try {
      await api.put(`/songs/${song.id}/agreement-made`, { made: made ? 1 : 0 });
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, agreement_made: made ? 1 : 0 } : s));
      message.success(made ? `"${song.title}" — agreement bana liya, ab Song Agreements me upload karo` : 'Tick hataya');
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const bulkAgreementMade = async (made) => {
    try {
      const res = await api.post('/songs/bulk-agreement-made', { ids: selectedRowKeys, made: made ? 1 : 0 });
      message.success(res.data.message || 'Updated');
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const publishSelectedToIntubeMusic = async () => {
    try {
      const res = await api.post('/songs/publish-intubemusic', { ids: selectedRowKeys });
      message.success(res.data.message || 'Queued');
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const downloadCategoryZip = (name) => {
    window.open(`${API_BASE}/api/songs/audio-zip?category=${encodeURIComponent(name)}&token=${authToken}`, '_blank');
  };

  const handleBulkDelete = async () => {
    try {
      const res = await api.post('/songs/bulk-delete', { ids: selectedRowKeys });
      message.success(res.data.message || 'Deleted');
      setSelectedRowKeys([]);
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Bulk delete failed'); }
  };

  const fetchSuggestions = async () => {
    try { const res = await api.get('/songs/name-suggestions'); setSuggestions(res.data || []); } catch (err) { /* table may not exist yet */ }
  };

  const applySuggestion = async (id) => {
    try {
      const res = await api.post('/songs/name-suggestions/apply', { id });
      message.success(`Name merged → ${res.data.title}`);
      setSuggestions(s => s.filter(x => x.id !== id));
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Merge failed'); }
  };

  const ignoreSuggestion = async (id) => {
    try {
      await api.post('/songs/name-suggestions/ignore', { id });
      setSuggestions(s => s.filter(x => x.id !== id));
    } catch (err) { message.error('Failed'); }
  };

  const fetchMergeSuggestions = async () => {
    try { const res = await api.get('/songs/merge-suggestions'); setMergeGroups(res.data?.groups || []); } catch (err) { /* ignore */ }
  };

  const mergeSongs = async (ids, preferredTitle) => {
    try {
      const payload = { ids };
      if (preferredTitle) payload.preferred_title = preferredTitle;
      const res = await api.post('/songs/merge', payload);
      message.success(`Merged ${res.data.merged_count + 1} songs → "${res.data.title}"`);
      setMergeGroups(g => g.filter(grp => !grp.some(s => ids.includes(s.id))));
      setMergingGroup(null);
      fetchSongs();
      fetchMergeSuggestions();
    } catch (err) { message.error(err.response?.data?.error || 'Merge failed'); }
  };

  const dismissMergeGroup = async (ids) => {
    try {
      await api.post('/songs/merge-suggestions/dismiss', { ids });
      message.success('Songs marked as separate — will not show again');
      setMergeGroups(g => g.filter(grp => {
        const grpIds = grp.map(s => s.id);
        return !ids.every(id => grpIds.includes(id));
      }));
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const exportExcel = (override) => {
    const f = override && override.search !== undefined ? override : filters;
    const p = new URLSearchParams();
    if (f.search) p.set('search', f.search);
    if (f.company_id) p.set('company_id', f.company_id);
    if (f.status) p.set('status', f.status);
    if (f.iprs) p.set('iprs', f.iprs);
    if (f.agreement) p.set('agreement', f.agreement);
    if (f.studio) p.set('studio', f.studio);
    if (f.no_poster) p.set('no_poster', '1');
    if (f.audio) p.set('audio', f.audio);
    if (f.category) p.set('category', f.category);
    p.set('token', authToken);
    window.open(`${API_BASE}/api/songs/export?${p.toString()}`, '_blank');
  };

  const deleteSongsDateRange = async (days) => {
    const label = days === 0 ? 'today' : `last ${days + 1} days`;
    try {
      const res = await api.delete('/songs/date-range', { data: { days } });
      message.success(res.data?.message || `Deleted songs from ${label}`);
      fetchSongs();
    } catch (err) { message.error(err.response?.data?.error || 'Date range delete failed'); }
  };

  const uploadConfig = {
    onUploadProgress: (e) => {
      if (!e.total) return;
      setUploadProgress(Math.round((e.loaded * 100) / e.total));
      const secs = (Date.now() - uploadStartRef.current) / 1000;
      setUploadInfo((u) => ({ ...u, loaded: e.loaded, total: e.total, speed: secs > 0 ? e.loaded / secs : 0 }));
    }
  };
  const fmtMb = (b) => `${(b / 1048576).toFixed(1)} MB`;
  const beginUpload = (title, fileName) => {
    uploadStartRef.current = Date.now();
    setUploadInfo({ title, file: fileName, loaded: 0, total: 0, speed: 0 });
    setUploading(true); setUploadProgress(0);
  };

  // Generic uploader that handles the backend's 409 "already uploaded" response by
  // asking the admin to confirm, then retrying with force=1.
  const uploadWithDupCheck = async (songId, file, field, after, songTitle = '') => {
    const send = async (force) => {
      const fd = new FormData();
      fd.append(field, file);
      if (force) fd.append('force', '1');
      return api.post(`/songs/${songId}/${field}`, fd, uploadConfig);
    };
    beginUpload(songTitle, file.name);
    try {
      const res = await send(false);
      setUploadProgress(100);
      message.success(`${field === 'audio' ? 'Audio' : 'Poster'} uploaded!`);
      after(res?.data);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.duplicate) {
        setUploading(false);
        Modal.confirm({
          title: 'Already uploaded',
          content: err.response.data.error,
          okText: 'Upload anyway',
          cancelText: 'Cancel',
          onOk: async () => {
            beginUpload(songTitle, file.name);
            try { const r2 = await send(true); setUploadProgress(100); message.success('Uploaded!'); after(r2?.data); }
            catch (e2) { message.error(e2.response?.data?.error || 'Upload failed'); }
            setUploading(false);
          },
        });
        return false;
      }
      message.error(err.response?.data?.error || `${field} upload failed`);
    }
    setUploading(false);
    return false;
  };

  const handleAudioUpload = (songId, file, songTitle = '') =>
    uploadWithDupCheck(songId, file, 'audio', () => fetchSongs(), songTitle);

  const handlePosterUpload = (songId, file, songTitle = '') =>
    uploadWithDupCheck(songId, file, 'poster', (resp) => {
      fetchSongs(); fetchSuggestions();
      if (resp?.path) setEditingSong(prev => prev && prev.id === songId ? { ...prev, poster_path: resp.path } : prev);
    });

  const deletePoster = async (songId) => {
    try {
      await api.post('/songs/bulk-delete-posters', { ids: [songId] });
      message.success('Poster deleted');
      fetchSongs();
      setEditingSong(prev => prev && prev.id === songId ? { ...prev, poster_path: null } : prev);
    } catch (err) {
      message.error(err.response?.data?.error || 'Poster delete failed');
    }
  };

  const downloadAudioByDate = () => {
    if (!audioDateRange?.[0] || !audioDateRange?.[1]) { message.info('Please select a date range first'); return; }
    const from = audioDateRange[0].format('YYYY-MM-DD');
    const to = audioDateRange[1].format('YYYY-MM-DD');
    window.open(`${API_BASE}/api/songs/audio-zip?from=${from}&to=${to}&token=${authToken}`, '_blank');
  };

  const isAudioFile = (f) => {
    const name = f.name || '';
    const type = f.type || f.originFileObj?.type || '';
    return /^audio\//i.test(type) || !/\.[a-z0-9]{1,5}$/i.test(name)
      || /\.(mp3|wav|flac|m4a|aac|ogg|oga|opus|wma|aif|aiff)$/i.test(name);
  };

  const dedupeFiles = (list) => {
    const seen = new Set();
    return list.filter(f => {
      const key = `${f.name}|${f.size ?? f.originFileObj?.size ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Dropping a folder (or a file the browser gives no MIME type for) is not handled by
  // antd's Dragger, so read the drop ourselves and walk directories.
  const readDroppedFiles = async (dataTransfer) => {
    const out = [];
    const entries = Array.from(dataTransfer.items || [])
      .map(it => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
      .filter(Boolean);
    const walk = async (entry) => {
      if (entry.isFile) {
        const file = await new Promise(res => entry.file(res, () => res(null)));
        if (file) out.push(file);
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        let batch;
        do {
          batch = await new Promise(res => reader.readEntries(res, () => res([])));
          for (const child of batch) await walk(child);
        } while (batch.length);
      }
    };
    for (const entry of entries) await walk(entry);
    if (!out.length) out.push(...Array.from(dataTransfer.files || []));
    return out;
  };

  const handleAudioDrop = async (e) => {
    const dropped = await readDroppedFiles(e.dataTransfer || {});
    if (!dropped.length) { message.error('Files read nahi ho payi — "click to browse" se select karo'); return; }
    const accepted = dropped.filter(isAudioFile);
    const skipped = dropped.length - accepted.length;
    if (accepted.length) {
      setBulkAudioFiles(prev => dedupeFiles([
        ...prev,
        ...accepted.map((file, i) => ({
          uid: `drop-${Date.now()}-${i}-${file.name}`,
          name: file.name, size: file.size, type: file.type, originFileObj: file,
        })),
      ]));
    }
    if (skipped) message.warning(`${skipped} file audio nahi thi, skip kar di`);
  };

  // Upload each audio in its own request so every file gets its own progress bar
  // (and one bad file never blocks the rest). The backend accepts audio_files[].
  const handleBulkAudio = async (values, skipTitles = []) => {
    const files = bulkAudioFiles;
    if (!files.length) { message.error('Please select audio files first'); return; }
    const company_id = values.company_id;
    const event_date = (bulkAudioDate || dayjs()).format('YYYY-MM-DD');
    setUploading(true);
    setAudioItems(files.map(f => ({ uid: f.uid, name: f.name, percent: 0, status: 'uploading' })));
    const setItem = (uid, patch) => setAudioItems(prev => prev.map(it => it.uid === uid ? { ...it, ...patch } : it));

    let ok = 0, fail = 0;
    const allDuplicates = [];
    await Promise.all(files.map(async (f) => {
      const fd = new FormData();
      fd.append('company_id', company_id);
      fd.append('event_date', event_date);
      if (values.category) fd.append('category', values.category);
      if (values.publish_intubemusic) fd.append('publish_intubemusic', '1');
      fd.append('audio_files[]', f.originFileObj);
      if (skipTitles.length) fd.append('skip_duplicate_check', JSON.stringify(skipTitles));
      try {
        const res = await api.post('/songs/bulk-upload', fd, {
          onUploadProgress: (e) => { if (e.total) setItem(f.uid, { percent: Math.round((e.loaded * 100) / e.total) }); }
        });
        if (res.data.duplicates && res.data.duplicates.length > 0) {
          allDuplicates.push(...res.data.duplicates);
          setItem(f.uid, { percent: 100, status: 'warning' });
        } else {
          ok++; setItem(f.uid, { percent: 100, status: 'done' });
        }
      } catch (err) { fail++; setItem(f.uid, { status: 'error' }); }
    }));

    setUploading(false);
    fetchSongs();

    if (allDuplicates.length > 0) {
      setDupItems(allDuplicates);
      setDupPendingUpload({ values, files: bulkAudioFiles });
      setDupModal(true);
      message.warning(`${allDuplicates.length} song(s) have similar titles — please review`);
    } else {
      if (ok) message.success(`${ok} audio uploaded${fail ? `, ${fail} failed` : ''}!`);
      else if (!fail) message.info('No new songs to upload');
      else message.error('Upload failed');
      if (!fail && !allDuplicates.length) {
        setTimeout(() => { setBulkAudioModal(false); bulkForm.resetFields(); setAudioItems([]); setBulkAudioFiles([]); }, 1400);
      }
    }
  };

  const handleDupConfirm = async (confirmedTitles) => {
    setDupModal(false);
    if (!confirmedTitles.length || !dupPendingUpload) return;
    const { values } = dupPendingUpload;
    await handleBulkAudio(values, confirmedTitles);
    setDupPendingUpload(null);
    setDupItems([]);
  };

  const openDetail = async (song) => {
    setDetailSong(song);
    setDetailVisible(true);
    try {
      const res = await api.get(`/songs/${song.id}`);
      setDetailSong(res.data);
    } catch (err) { /* keep row data */ }
  };

  const updateIprs = async (song, iprs_status) => {
    try {
      await api.put(`/songs/${song.id}/iprs-status`, { iprs_status });
      message.success('IPRS status updated');
      fetchSongs();
      if (detailSong && detailSong.id === song.id) setDetailSong({ ...detailSong, iprs_status });
    } catch (err) { message.error('Failed to update IPRS status'); }
  };

  const updateDistribution = async (song, distribution_status) => {
    try {
      await api.put(`/songs/${song.id}/distribution-status`, { distribution_status });
      message.success('Distribution status updated');
      fetchSongs();
      if (detailSong && detailSong.id === song.id) setDetailSong({ ...detailSong, distribution_status });
    } catch (err) { message.error('Failed to update distribution status'); }
  };

  const isPlaying = (id) => globalPlayer.track?.id === id && globalPlayer.playing;

  const togglePlay = (song) => {
    const url = `${API_BASE}/api/songs/${song.id}/stream`;
    globalPlayer.play(song.id, song.title || 'Unknown', url, song.singer);
  };

  const statusColors = { distributed: 'green', pending: 'orange', not_distributed: 'red' };
  const statusLabels = { distributed: 'Distributed', pending: 'Pending', not_distributed: 'Not Distributed' };
  const iprsColors = { claimed: 'green', pending: 'volcano', not_applicable: 'default' };
  const iprsLabels = { claimed: 'IPRS Claimed', pending: 'IPRS Pending', not_applicable: 'N/A' };
  const ytColors = { uploaded: 'red', not_uploaded: 'default', scheduled: 'blue' };
  const ytLabels = { uploaded: 'On YouTube', not_uploaded: 'Not Uploaded', scheduled: 'Scheduled' };

  const downloadItems = (id) => [
    { key: 'mp3', label: <a href={`${API_BASE}/api/songs/${id}/download?fmt=mp3&token=${authToken}`} target="_blank" rel="noreferrer">MP3 download</a> },
    { key: 'wav', label: <a href={`${API_BASE}/api/songs/${id}/download?fmt=wav&token=${authToken}`} target="_blank" rel="noreferrer">WAV download</a> },
  ];

  const canEdit = ['admin', 'super_admin', 'manager'].includes(userRole);
  const canUploadPoster = ['admin', 'super_admin'].includes(userRole) || hasPerm(user, 'thumbnail_upload');
  const canUploadAudio = ['admin', 'super_admin', 'distribution_staff'].includes(userRole) || hasPerm(user, 'audio_upload');
  const canStatus = ['admin', 'super_admin', 'manager', 'distribution_staff'].includes(userRole) || hasPerm(user, 'audio_upload');

  const columns = [
    {
      title: 'Poster', dataIndex: 'poster_path', width: 90,
      render: (p, r) => p ? (
        <Space direction="vertical" size={2} align="center">
          <Image src={`${API_BASE}/${p}`} width={40} height={40} style={{ borderRadius: 4, objectFit: 'cover' }} />
          <Tag color="success" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Uploaded</Tag>
        </Space>
      ) : (canUploadPoster ?
        <Upload showUploadList={false} beforeUpload={(f) => handlePosterUpload(r.id, f, r.title)}>
          <Button size="small" icon={<UploadOutlined />} />
        </Upload> : <Tag color="warning" style={{ marginInlineEnd: 0 }}>Pending</Tag>)
    },
    { title: 'Title', dataIndex: 'title', key: 'title', render: (t, r) => {
        if (editingTitleId === r.id) {
          return <Input size="small" value={editTitleVal} onChange={e => setEditTitleVal(e.target.value)}
            onPressEnter={() => handleSongTitleEdit(r.id)} onBlur={() => handleSongTitleEdit(r.id)} autoFocus style={{ width: '100%' }} />;
        }
        const missing = [];
        if (!r.isrc) missing.push('ISRC');
        if (!r.upc) missing.push('UPC');
        if (!r.audio_path) missing.push('Audio');
        if (!r.poster_path) missing.push('Poster');
        return (
          <Space size={4}>
            <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 600 }} onClick={() => openDetail(r)}>{t}</Button>
            <EditOutlined style={{ cursor: 'pointer', color: '#1890ff', fontSize: 12 }} onClick={() => { setEditingTitleId(r.id); setEditTitleVal(t); }} />
            {missing.length > 0 && <Tooltip title={`Please upload: ${missing.join(', ')}`}><Tag color="orange" style={{ marginInlineEnd: 0 }}>Incomplete</Tag></Tooltip>}
          </Space>
        );
      }
    },
    {
      title: 'Audio', key: 'audio', width: 170,
      render: (_, r) => r.audio_path ? (
        <Space size={4}>
          <Tag color="success" icon={<CheckCircleFilled />}>Uploaded</Tag>
          <Button size="small" type={isPlaying(r.id) ? 'primary' : 'default'} icon={isPlaying(r.id) ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={() => togglePlay(r)} />
          {canDownloadAudio && (
            <Dropdown menu={{ items: downloadItems(r.id) }} trigger={['click']}>
              <Tooltip title="Download MP3 / WAV"><Button size="small" icon={<DownloadOutlined />} /></Tooltip>
            </Dropdown>
          )}
        </Space>
      ) : (
        <Space size={4}>
          <Tag color="warning">Pending</Tag>
          {canUploadAudio && <Upload showUploadList={false} beforeUpload={(f) => handleAudioUpload(r.id, f, r.title)}>
            <Tooltip title={`"${r.title}" ka audio upload karo`}><Button size="small" type="primary" ghost icon={<UploadOutlined />}>Upload</Button></Tooltip>
          </Upload>}
        </Space>
      )
    },
    { title: 'Singer', dataIndex: 'singer', key: 'singer' },
    { title: 'ISRC', dataIndex: 'isrc', key: 'isrc', render: (v) => v || '-' },
    { title: 'UPC', dataIndex: 'upc', key: 'upc', render: (v) => v || '-' },
    { title: 'Company', dataIndex: 'company_name', key: 'company' },
    { title: 'Studio', dataIndex: 'studio', key: 'studio', width: 160, render: v => v || '-' },
    {
      title: 'Section', dataIndex: 'category', key: 'category', width: 170,
      render: (v, r) => canEdit ? (
        <Select size="small" style={{ width: 160 }} value={v || undefined} placeholder="No section" allowClear
          showSearch options={categoryOptions} onChange={(val) => setSongCategory(r, val || '')} />
      ) : (v ? <Tag color="purple">{v}</Tag> : '-')
    },
    {
      title: 'Distribution', dataIndex: 'distribution_status', key: 'status', width: 160,
      render: (s, r) => canStatus ? (
        <Select size="small" value={s} style={{ width: 150 }} onChange={(v) => updateDistribution(r, v)}
          options={[
            { value: 'distributed', label: 'Distributed' },
            { value: 'pending', label: 'Pending' },
            { value: 'not_distributed', label: 'Not Distributed' },
          ]} />
      ) : <Tag color={statusColors[s]}>{statusLabels[s]}</Tag>
    },
    {
      title: 'IPRS Claim', dataIndex: 'iprs_status', key: 'iprs', width: 150,
      render: (s, r) => canStatus ? (
        <Select size="small" value={s || 'pending'} style={{ width: 140 }} onChange={(v) => updateIprs(r, v)}
          options={[
            { value: 'claimed', label: 'Claimed' },
            { value: 'pending', label: 'Pending' },
            { value: 'not_applicable', label: 'N/A' },
          ]} />
      ) : <Tag color={iprsColors[s] || 'default'}>{iprsLabels[s] || 'N/A'}</Tag>
    },
    {
      title: 'Agreement', key: 'agreement', width: 210,
      render: (_, r) => {
        const uploaded = ['singer_noc', 'studio_noc', 'singer_agreement', 'studio_agreement'].filter(t => Number(r[`has_${t}`]) > 0);
        const made = Number(r.agreement_made) === 1;
        const shortNames = { singer_noc: 'Singer NOC', studio_noc: 'Studio NOC', singer_agreement: 'Singer Agr', studio_agreement: 'Studio Agr' };
        return (
          <Space direction="vertical" size={2}>
            {canEdit ? (
              <Checkbox checked={made || uploaded.length > 0} disabled={uploaded.length > 0}
                onChange={(e) => setAgreementMade(r, e.target.checked)}>
                <span style={{ fontSize: 12 }}>{uploaded.length > 0 ? 'Uploaded' : made ? 'Bana — upload pending' : 'Agreement bana liya?'}</span>
              </Checkbox>
            ) : (
              <Tag color={uploaded.length > 0 ? 'green' : made ? 'orange' : 'default'}>{uploaded.length > 0 ? 'Uploaded' : made ? 'Made — pending upload' : 'Not made'}</Tag>
            )}
            {uploaded.length > 0 && (
              <Space size={2} wrap>
                {uploaded.map(t => <Tag key={t} color="green" style={{ margin: 0, cursor: 'pointer', fontSize: 11 }} onClick={() => openDetail(r)}>{shortNames[t]}</Tag>)}
                {Number(r.agreement_count) > uploaded.length && <Tag style={{ margin: 0, fontSize: 11, cursor: 'pointer' }} onClick={() => openDetail(r)}>+{Number(r.agreement_count) - uploaded.length} other</Tag>}
              </Space>
            )}
          </Space>
        );
      }
    },
    {
      title: 'YouTube', dataIndex: 'youtube_status', key: 'yt_status',
      render: (s) => s ? <Tag color={ytColors[s]} icon={s === 'uploaded' ? <YoutubeOutlined /> : null}>{ytLabels[s] || 'N/A'}</Tag> : <Tag>N/A</Tag>
    },
    ...(canEdit ? [{
      title: 'Actions', key: 'actions', width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingSong(r); form.setFieldsValue(r); setModalVisible(true); }} />
          {userRole === 'admin' && <Popconfirm title="Delete this song?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>}
        </Space>
      )
    }] : [])
  ];

  return (
    <div>
      <Modal open={uploading} footer={null} closable={false} maskClosable={false} title="Uploading..." width={440}>
        {uploadInfo.title && <div style={{ fontWeight: 600, marginBottom: 2 }}>{uploadInfo.title}</div>}
        {uploadInfo.file && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{uploadInfo.file}</div>}
        <Progress percent={uploadProgress} status={uploadProgress < 100 ? 'active' : 'success'} />
        <Text type="secondary">
          {uploadProgress < 100
            ? `${fmtMb(uploadInfo.loaded)} / ${fmtMb(uploadInfo.total)} · ${fmtMb(uploadInfo.speed)}/s${uploadInfo.speed > 0 ? ` · ~${Math.max(1, Math.round((uploadInfo.total - uploadInfo.loaded) / uploadInfo.speed))}s baki` : ''} — window band mat karo`
            : 'Processing on server...'}
        </Text>
      </Modal>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={3} style={{ margin: 0 }}>Song Library <Tag color="blue" style={{ fontSize: 14, verticalAlign: 'middle' }}>{total} songs</Tag></Title></Col>
        <Col>
          <Space>
            {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingSong(null); form.resetFields(); setModalVisible(true); }}>Add Song</Button>}
            {canUploadAudio && <Button icon={<CloudUploadOutlined />} onClick={() => setBulkAudioModal(true)}>Bulk Audio Upload</Button>}
            {/* Bulk Poster Upload removed — use Thumbnail Library instead */}
            {canEdit && (
              <Badge count={suggestions.length} size="small">
                <Button icon={<EditOutlined />} onClick={() => setSuggestModal(true)}>Name Suggestions</Button>
              </Badge>
            )}
            {['admin', 'super_admin'].includes(userRole) && (
              <Badge count={mergeGroups.length} size="small">
                <Button icon={<CustomerServiceOutlined />} onClick={() => { fetchMergeSuggestions(); setMergeModalVisible(true); }} type={mergeGroups.length > 0 ? 'primary' : 'dashed'} danger={mergeGroups.length > 0}>Merge Suggestions</Button>
              </Badge>
            )}
            {canEdit && <Button icon={<DownloadOutlined />} onClick={exportExcel}>Download Excel</Button>}
            {canDownloadAudio && <RangePicker value={audioDateRange} onChange={setAudioDateRange} format="YYYY-MM-DD" placeholder={['From date', 'To date']} />}
            {canDownloadAudio && <Button icon={<DownloadOutlined />} onClick={downloadAudioByDate} disabled={!audioDateRange?.[0]}>Download Audio (date ZIP)</Button>}
            {userRole === 'admin' && (
              <>
                <Text type="secondary">Quick Delete:</Text>
                <Popconfirm title="Delete all songs uploaded today?" onConfirm={() => deleteSongsDateRange(0)} okText="Yes" okButtonProps={{ danger: true }}>
                  <Button danger size="small">Today</Button>
                </Popconfirm>
                <Popconfirm title="Delete all songs uploaded in last 2 days?" onConfirm={() => deleteSongsDateRange(1)} okText="Yes" okButtonProps={{ danger: true }}>
                  <Button danger size="small">2 Days</Button>
                </Popconfirm>
                <Popconfirm title="Delete all songs uploaded in last 3 days?" onConfirm={() => deleteSongsDateRange(2)} okText="Yes" okButtonProps={{ danger: true }}>
                  <Button danger size="small">3 Days</Button>
                </Popconfirm>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8} sm={6}>
          <Card onClick={() => setFilters({ ...filters, status: '', page: 1 })} hoverable
            style={{ borderRadius: 10, background: '#e6f7ff', textAlign: 'center', border: !filters.status ? '2px solid #1890ff' : undefined, cursor: 'pointer' }}>
            <Statistic title="Total Songs" value={counts.total} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card onClick={() => setFilters({ ...filters, status: 'distributed', page: 1 })} hoverable
            style={{ borderRadius: 10, background: '#f6ffed', textAlign: 'center', border: filters.status === 'distributed' ? '2px solid #52c41a' : undefined, cursor: 'pointer' }}>
            <Statistic title="Distributed" value={counts.distributed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card onClick={() => setFilters({ ...filters, status: 'not_done', page: 1 })} hoverable
            style={{ borderRadius: 10, background: '#fff7e6', textAlign: 'center', border: (filters.status === 'not_done' || filters.status === 'pending' || filters.status === 'not_distributed') ? '2px solid #faad14' : undefined, cursor: 'pointer' }}>
            <Statistic title="Not Distributed" value={counts.pending} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8} sm={6}>
          <Card onClick={() => setFilters({ ...filters, agreement: 'not_made', page: 1 })} hoverable
            style={{ borderRadius: 10, background: '#fff1f0', textAlign: 'center', border: filters.agreement === 'not_made' ? '2px solid #ff4d4f' : undefined, cursor: 'pointer' }}>
            <Statistic title="Agreement Nahi Bana" value={agreementCounts.not_made} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card onClick={() => setFilters({ ...filters, agreement: 'made_pending', page: 1 })} hoverable
            style={{ borderRadius: 10, background: '#fff7e6', textAlign: 'center', border: filters.agreement === 'made_pending' ? '2px solid #faad14' : undefined, cursor: 'pointer' }}>
            <Statistic title="Bana — Upload Pending" value={agreementCounts.made_pending} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card onClick={() => setFilters({ ...filters, agreement: 'uploaded', page: 1 })} hoverable
            style={{ borderRadius: 10, background: '#f6ffed', textAlign: 'center', border: filters.agreement === 'uploaded' ? '2px solid #52c41a' : undefined, cursor: 'pointer' }}>
            <Statistic title="Agreement Uploaded" value={agreementCounts.uploaded} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* Audio upload status — how many songs have an audio file and how many still need one */}
      <Card style={{ marginBottom: 16, borderRadius: 10 }}
        title={<span><SoundOutlined style={{ color: '#1890ff', marginRight: 8 }} />Audio Upload Status</span>}
        extra={filters.audio && <Tag color="blue" closable onClose={() => setFilters({ ...filters, audio: '', page: 1 })}>Showing: {filters.audio === 'uploaded' ? 'Audio Uploaded' : 'Audio Not Uploaded'}</Tag>}>
        <Row gutter={16}>
          <Col xs={8} sm={6}>
            <Card onClick={() => setFilters({ ...filters, audio: '', page: 1 })} hoverable
              style={{ borderRadius: 10, background: '#e6f7ff', textAlign: 'center', border: !filters.audio ? '2px solid #1890ff' : undefined, cursor: 'pointer' }}>
              <Statistic title="Total Songs" value={audioCounts.total} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col xs={8} sm={6}>
            <Card onClick={() => setFilters({ ...filters, audio: 'uploaded', page: 1 })} hoverable
              style={{ borderRadius: 10, background: '#f6ffed', textAlign: 'center', border: filters.audio === 'uploaded' ? '2px solid #52c41a' : undefined, cursor: 'pointer' }}>
              <Statistic title="Audio Uploaded" value={audioCounts.uploaded} valueStyle={{ color: '#52c41a' }}
                suffix={audioCounts.total ? <span style={{ fontSize: 14, color: '#888' }}>({Math.round(audioCounts.uploaded * 100 / audioCounts.total)}%)</span> : null} />
            </Card>
          </Col>
          <Col xs={8} sm={6}>
            <Card onClick={() => setFilters({ ...filters, audio: 'not_uploaded', page: 1 })} hoverable
              style={{ borderRadius: 10, background: '#fff1f0', textAlign: 'center', border: filters.audio === 'not_uploaded' ? '2px solid #ff4d4f' : undefined, cursor: 'pointer' }}>
              <Statistic title="Audio Not Uploaded" value={audioCounts.not_uploaded} valueStyle={{ color: '#ff4d4f' }} />
            </Card>
          </Col>
          <Col xs={24} sm={6} style={{ display: 'flex', alignItems: 'center' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Progress percent={audioCounts.total ? Math.round(audioCounts.uploaded * 100 / audioCounts.total) : 0} status="active" strokeColor="#52c41a" />
              {canEdit && (
                <Button size="small" icon={<DownloadOutlined />} onClick={() => { setFilters({ ...filters, audio: 'not_uploaded', page: 1 }); exportExcel({ ...filters, audio: 'not_uploaded' }); }}>
                  Not Uploaded list (Excel)
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Audio Library sections — click a section to open its songs */}
      <Card style={{ marginBottom: 16, borderRadius: 10 }}
        title={<span><AppstoreOutlined style={{ color: '#7c3aed', marginRight: 8 }} />Sections</span>}
        extra={canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => { setNewCatName(''); setNewCatModal(true); }}>+ Naya Section Banao</Button>}>
        <Space wrap size={[10, 10]}>
          <Button type={!filters.category ? 'primary' : 'default'} onClick={() => setFilters({ ...filters, category: '', page: 1 })}>
            All Songs ({allTotal})
          </Button>
          {categories.map(c => (
            <Space.Compact key={c.name}>
              <Button type={filters.category === c.name ? 'primary' : 'default'}
                onClick={() => setFilters({ ...filters, category: c.name, page: 1 })}>
                {c.name} ({c.total})
              </Button>
              {canDownloadAudio && (
                <Tooltip title={`${c.name} ke saare audio ek ZIP me download`}>
                  <Button icon={<DownloadOutlined />} disabled={!c.with_audio} onClick={() => downloadCategoryZip(c.name)} />
                </Tooltip>
              )}
            </Space.Compact>
          ))}
          <Button type={filters.category === '__none__' ? 'primary' : 'dashed'}
            onClick={() => setFilters({ ...filters, category: '__none__', page: 1 })}>
            No Section ({uncategorized})
          </Button>
        </Space>
      </Card>

      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Input placeholder="Search song, singer, ISRC, UPC..." prefix={<SearchOutlined />} value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })} allowClear />
          </Col>
          <Col xs={24} sm={8}>
            <Select placeholder="Filter by Company" value={filters.company_id || undefined} onChange={(v) => setFilters({ ...filters, company_id: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select placeholder="Filter by Distribution" value={filters.status || undefined} onChange={(v) => setFilters({ ...filters, status: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              <Option value="distributed">Distributed</Option>
              <Option value="not_done">Not Distributed (All Pending)</Option>
              <Option value="pending">Pending</Option>
              <Option value="not_distributed">Not Distributed</Option>
            </Select>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by Studio" value={filters.studio || undefined} onChange={(v) => setFilters({ ...filters, studio: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              {studioOptions.map(s => <Option key={s} value={s}>{s}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by Section" value={filters.category || undefined} onChange={(v) => setFilters({ ...filters, category: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              {categories.map(c => <Option key={c.name} value={c.name}>{c.name} ({c.total})</Option>)}
              <Option value="__none__">No Section</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by IPRS Claim" value={filters.iprs || undefined} onChange={(v) => setFilters({ ...filters, iprs: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              <Option value="claimed">IPRS Claimed</Option>
              <Option value="pending">IPRS Pending</Option>
              <Option value="not_applicable">N/A</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by Audio" value={filters.audio || undefined} onChange={(v) => setFilters({ ...filters, audio: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              <Option value="uploaded">Audio Uploaded ({audioCounts.uploaded})</Option>
              <Option value="not_uploaded">Audio Not Uploaded ({audioCounts.not_uploaded})</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by Agreement" value={filters.agreement || undefined} onChange={(v) => setFilters({ ...filters, agreement: v || '', page: 1 })} allowClear style={{ width: '100%' }}>
              <Option value="not_made">Agreement Nahi Bana ({agreementCounts.not_made})</Option>
              <Option value="made_pending">Bana — Upload Pending ({agreementCounts.made_pending})</Option>
              <Option value="made">Bana (pending + uploaded)</Option>
              <Option value="uploaded">Uploaded ({agreementCounts.uploaded})</Option>
              <Option value="missing_singer_noc">Singer NOC missing</Option>
              <Option value="missing_studio_noc">Studio NOC missing</Option>
              <Option value="missing_singer_agreement">Singer Agreement missing</Option>
              <Option value="missing_studio_agreement">Studio Agreement missing</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker format="DD/MM/YYYY" placeholder={['Upload From', 'Upload To']}
              onChange={(dates) => {
                const from = dates?.[0]?.format('YYYY-MM-DD') || '';
                const to = dates?.[1]?.format('YYYY-MM-DD') || '';
                setFilters({ ...filters, from, to, page: 1 });
              }}
              allowClear style={{ width: '100%' }} />
          </Col>
        </Row>
        {filters.no_poster && (
          <Row style={{ marginTop: 12 }}>
            <Col>
              <Tag color="orange" closable onClose={() => setFilters({ ...filters, no_poster: '', page: 1 })}>Showing: Songs without Poster</Tag>
              <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={exportExcel} style={{ marginLeft: 8 }}>Export No-Poster List (Excel)</Button>
            </Col>
          </Row>
        )}
        {filters.status === 'not_done' && (
          <Row style={{ marginTop: 12 }}>
            <Col>
              <Tag color="red" closable onClose={() => setFilters({ ...filters, status: '', page: 1 })}>Showing: Not Distributed Songs ({total})</Tag>
              <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={exportExcel} style={{ marginLeft: 8 }}>Export Not-Distributed List (Excel)</Button>
            </Col>
          </Row>
        )}
      </Card>

      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Space wrap>
            <Text strong>{selectedRowKeys.length} selected</Text>
            {canDownloadAudio && (
              <Button type="primary" icon={<DownloadOutlined />} onClick={() => {
                window.open(`${API_BASE}/api/songs/audio-zip?ids=${selectedRowKeys.join(',')}&token=${authToken}`, '_blank');
              }}>Download Selected Audio (ZIP)</Button>
            )}
            {canEdit && (
              <AutoComplete style={{ width: 210 }} options={categoryOptions} value={bulkCategoryValue}
                onChange={setBulkCategoryValue} onSelect={(v) => bulkMoveToCategory(v)}
                placeholder="Move selected to section…"
                filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())} />
            )}
            {canEdit && bulkCategoryValue && (
              <Button onClick={() => bulkMoveToCategory(bulkCategoryValue.trim())}>Move</Button>
            )}
            {canEdit && (
              <Button icon={<FileProtectOutlined />} onClick={() => bulkAgreementMade(true)}>Agreement Bana Liya (tick)</Button>
            )}
            {canEdit && (
              <Popconfirm title={`Publish ${selectedRowKeys.length} song(s) to Intube Music?`} okText="Publish" onConfirm={publishSelectedToIntubeMusic}>
                <Button icon={<CloudServerOutlined />}>Publish to Intube Music</Button>
              </Popconfirm>
            )}
            {userRole === 'admin' && (
              <Popconfirm title={`Delete ${selectedRowKeys.length} selected song(s)? This cannot be undone.`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={handleBulkDelete}>
                <Button danger icon={<DeleteOutlined />}>Delete Selected</Button>
              </Popconfirm>
            )}
            <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
          </Space>
        </div>
      )}

      <Table
        dataSource={songs} columns={columns} rowKey="id" loading={loading} size="middle"
        rowSelection={(userRole === 'admin' || canDownloadAudio) ? { selectedRowKeys, onChange: setSelectedRowKeys, preserveSelectedRowKeys: true } : undefined}
        pagination={{ current: filters.page, total, pageSize: 50, onChange: (p) => setFilters({ ...filters, page: p }) }}
        scroll={{ x: 1600 }}
        rowClassName={(r) => r.distribution_status === 'distributed' ? 'row-distributed' : ''}
      />

      {/* Add/Edit Song Modal */}
      <Modal title={editingSong ? 'Edit Song' : 'Add Song'} open={modalVisible} onCancel={() => { setModalVisible(false); setEditingSong(null); }}
        onOk={() => form.submit()} width={700}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company_id" label="Company" rules={[{ required: true }]}>
                <Select>{companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="singer" label="Singer"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="artist" label="Artist"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="lyricist" label="Lyricist"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="composer" label="Composer"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="isrc" label="ISRC"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="upc" label="UPC"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="genre" label="Genre"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="language" label="Language"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="distributor" label="Distributor"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Section (Category)" tooltip="Devotional, Gurjar Rasiya, Rajasthani, Sad Song, DJ Song, Remix Song — ya naya naam likh do">
                <AutoComplete options={categoryOptions} allowClear placeholder="Select or type a section"
                  filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="publish_intubemusic" valuePropName="checked" label="Intube Music">
                <Checkbox>Publish this song on intubemusic.com</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="distribution_status" label="Distribution">
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="distributed">Distributed</Option>
                  <Option value="not_distributed">Not Distributed</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="iprs_status" label="IPRS Claim">
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="claimed">Claimed</Option>
                  <Option value="not_applicable">N/A</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}><Form.Item name="duration" label="Duration"><Input placeholder="3:45" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="release_date" label="Release Date"><Input type="date" /></Form.Item></Col>
            <Col span={12}><Form.Item name="distribution_date" label="Distribution Date"><Input type="date" /></Form.Item></Col>
          </Row>
          {editingSong && (
            <Form.Item label="Poster / Thumbnail">
              <Space align="start" wrap>
                {editingSong.poster_path ? (
                  <Image
                    src={`${API_BASE}/${editingSong.poster_path}`}
                    width={90} height={90}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                    fallback=""
                  />
                ) : <Tag color="orange">No poster</Tag>}
                <Space direction="vertical">
                  <Upload showUploadList={false} beforeUpload={(f) => { handlePosterUpload(editingSong.id, f); return false; }}>
                    <Button icon={<UploadOutlined />} size="small">{editingSong.poster_path ? 'Replace Poster' : 'Upload Poster'}</Button>
                  </Upload>
                  {editingSong.poster_path && (
                    <Popconfirm title="Delete this poster?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => deletePoster(editingSong.id)}>
                      <Button danger icon={<DeleteOutlined />} size="small">Delete Poster</Button>
                    </Popconfirm>
                  )}
                </Space>
              </Space>
              <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>Poster upload karte hi Music Distribution me bhi update ho jayega.</div>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Song Detail Drawer */}
      <Drawer title={detailSong?.title || 'Song Details'} open={detailVisible} onClose={() => setDetailVisible(false)} width={480}>
        {detailSong && (
          <>
            {detailSong.poster_path && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Image src={`${API_BASE}/${detailSong.poster_path}`} width={160} height={160} style={{ borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
            <Space style={{ marginBottom: 16 }} wrap>
              <Tag color={statusColors[detailSong.distribution_status]}>{statusLabels[detailSong.distribution_status]}</Tag>
              <Tag color={iprsColors[detailSong.iprs_status] || 'default'}>{iprsLabels[detailSong.iprs_status] || 'IPRS N/A'}</Tag>
              {Number(detailSong.agreement_count) > 0 && <Tag color="blue" icon={<FileProtectOutlined />}>Agreement</Tag>}
            </Space>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Title">{detailSong.title}</Descriptions.Item>
              <Descriptions.Item label="Singer">{detailSong.singer || '-'}</Descriptions.Item>
              <Descriptions.Item label="Artist">{detailSong.artist || '-'}</Descriptions.Item>
              <Descriptions.Item label="Composer">{detailSong.composer || '-'}</Descriptions.Item>
              <Descriptions.Item label="Lyricist">{detailSong.lyricist || '-'}</Descriptions.Item>
              <Descriptions.Item label="ISRC">{detailSong.isrc || '-'}</Descriptions.Item>
              <Descriptions.Item label="UPC">{detailSong.upc || '-'}</Descriptions.Item>
              <Descriptions.Item label="Genre">{detailSong.genre || '-'}</Descriptions.Item>
              <Descriptions.Item label="Language">{detailSong.language || '-'}</Descriptions.Item>
              <Descriptions.Item label="Company">{detailSong.company_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Distributor">{detailSong.distributor || '-'}</Descriptions.Item>
              <Descriptions.Item label="Distribution">{statusLabels[detailSong.distribution_status]}{detailSong.distribution_date ? ` (${detailSong.distribution_date})` : ''}</Descriptions.Item>
              <Descriptions.Item label="IPRS Claim">{iprsLabels[detailSong.iprs_status] || 'N/A'}{detailSong.iprs_date ? ` (${detailSong.iprs_date})` : ''}</Descriptions.Item>
              <Descriptions.Item label="Release Date">{detailSong.release_date || '-'}</Descriptions.Item>
            </Descriptions>
            {detailSong.agreements && detailSong.agreements.length > 0 && (
              <>
                <Title level={5} style={{ marginTop: 16 }}>Agreements</Title>
                {detailSong.agreements.map(a => (
                  <Card key={a.id} size="small" style={{ marginBottom: 8 }}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text strong>{a.title}</Text>
                      {a.party && <Text type="secondary">Party: {a.party}</Text>}
                      <Space wrap>
                        <Tag color={a.status === 'done' ? 'green' : 'orange'}>{a.status === 'done' ? 'Done' : 'Pending'}</Tag>
                        {a.file_path && <Button size="small" icon={<DownloadOutlined />} href={`${API_BASE}/api/agreements/${a.id}/download?token=${authToken}`} target="_blank">Download</Button>}
                        {a.file_path && <Button size="small" type="primary" icon={<FileProtectOutlined />} onClick={() => window.open(`${API_BASE}/api/agreements/${a.id}/view?token=${authToken}`, '_blank')}>View PDF</Button>}
                      </Space>
                    </Space>
                  </Card>
                ))}
              </>
            )}
            {detailSong.audio_path && (
              <div style={{ marginTop: 16 }}>
                <audio controls src={`${API_BASE}/api/songs/${detailSong.id}/stream`} style={{ width: '100%' }} />
                {canDownloadAudio && (
                  <Dropdown menu={{ items: downloadItems(detailSong.id) }} trigger={['click']}>
                    <Button block style={{ marginTop: 8 }} icon={<DownloadOutlined />}>Download Audio (MP3 / WAV)</Button>
                  </Dropdown>
                )}
              </div>
            )}
          </>
        )}
      </Drawer>

      {/* Bulk Audio Upload Modal */}
      <Modal
        title={<span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 18 }}><CustomerServiceOutlined style={{ color: '#7c3aed', marginRight: 8 }} />Bulk Audio Upload</span>}
        open={bulkAudioModal}
        onCancel={() => { if (!uploading) { setBulkAudioModal(false); setAudioItems([]); setBulkAudioFiles([]); } }}
        maskClosable={!uploading}
        width={560}
        okText={uploading ? 'Uploading…' : 'Upload'}
        okButtonProps={{ loading: uploading }}
        cancelButtonProps={{ disabled: uploading }}
        onOk={() => {
          if (!bulkAudioFiles.length) { message.error('Pehle audio files select karo'); return; }
          bulkForm.validateFields().then(v => handleBulkAudio(v)).catch(() => message.error('Company select karo'));
        }}
      >
        <Form form={bulkForm} layout="vertical" onFinish={handleBulkAudio}>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="company_id" label="Company" rules={[{ required: true, message: 'Select company' }]}>
                <Select placeholder="Select company">{companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Upload Date">
                <DatePicker value={bulkAudioDate} onChange={(d) => setBulkAudioDate(d || dayjs())} allowClear={false} format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="category" label="Section (Category)">
                <AutoComplete options={categoryOptions} allowClear placeholder="Devotional, DJ Song… (in sab files ke liye)"
                  filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(p, c) => p.category !== c.category}>
                {({ getFieldValue }) => /remix/i.test(getFieldValue('category') || '') ? (
                  <div style={{ marginTop: -16, marginBottom: 12, fontSize: 12, color: '#7c3aed' }}>
                    Remix: har file alag song banegi (same naam ka gaana ho to bhi merge nahi hoga, title me "(Remix)" lagega) aur agreement automatic nahi banega.
                  </div>
                ) : null}
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="publish_intubemusic" valuePropName="checked" label="Intube Music">
                <Checkbox>Publish on intubemusic.com</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Audio Files">
            <Dragger
              multiple
              beforeUpload={() => false}
              showUploadList={false}
              disabled={uploading}
              fileList={bulkAudioFiles}
              onDrop={handleAudioDrop}
              onChange={({ fileList }) => setBulkAudioFiles(dedupeFiles(fileList.filter(isAudioFile)))}
              style={{ borderRadius: 12, background: 'linear-gradient(135deg,#faf5ff 0%,#eef2ff 100%)', borderColor: '#c4b5fd' }}
            >
              <p style={{ margin: 0 }}><InboxOutlined style={{ fontSize: 40, color: '#7c3aed' }} /></p>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 15, margin: '8px 0 2px' }}>Drag audio files here, or click to browse</p>
              <p style={{ color: '#8b8b9e', fontSize: 12, margin: 0 }}>MP3, WAV, FLAC, M4A • file name becomes the song title</p>
            </Dragger>
          </Form.Item>

          {bulkAudioFiles.length > 0 && (
            <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bulkAudioFiles.map(f => {
                const it = audioItems.find(a => a.uid === f.uid);
                const status = it?.status || 'ready';
                const percent = it?.percent || 0;
                const ring = status === 'error' ? '#ff4d4f' : status === 'done' ? '#52c41a' : '#7c3aed';
                return (
                  <div key={f.uid} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#fff', border: '1px solid #eef0f5', borderRadius: 10, boxShadow: '0 1px 4px rgba(60,70,120,0.05)' }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: `${ring}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {status === 'done' ? <CheckCircleFilled style={{ color: '#52c41a' }} />
                        : status === 'error' ? <CloseCircleFilled style={{ color: '#ff4d4f' }} />
                        : <CustomerServiceOutlined style={{ color: '#7c3aed' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                      <Progress percent={percent} size="small" showInfo={false}
                        status={status === 'error' ? 'exception' : status === 'done' ? 'success' : 'active'}
                        strokeColor={status === 'uploading' ? { from: '#7c3aed', to: '#4f46e5' } : undefined} />
                    </div>
                    <div style={{ width: 48, textAlign: 'right', fontSize: 12, fontWeight: 600, color: status === 'error' ? '#ff4d4f' : '#6b7280' }}>
                      {status === 'error' ? 'Failed' : status === 'done' ? 'Done' : `${percent}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Form>
      </Modal>

      {/* New Section Modal */}
      <Modal title="New Section" open={newCatModal} onCancel={() => setNewCatModal(false)} onOk={addCategory} okText="Add">
        <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="e.g. Bhajan, Holi Special" onPressEnter={addCategory} />
      </Modal>

      {/* Bulk Poster Upload Modal */}
      <Modal title="Bulk Poster Upload" open={bulkPosterModal} onCancel={() => setBulkPosterModal(false)}
        onOk={() => { const el = document.getElementById('poster-form'); if (el) el.dispatchEvent(new Event('submit', { bubbles: true })); }}
        footer={[
          <Button key="cancel" onClick={() => setBulkPosterModal(false)}>Cancel</Button>,
          <Button key="submit" type="primary" onClick={() => {
            const fileInput = document.querySelector('#bulk-poster-upload input[type="file"]');
            if (fileInput && fileInput.files.length > 0) {
              const formData = new FormData();
              Array.from(fileInput.files).forEach(f => formData.append('poster_files[]', f));
              formData.append('event_date', (bulkPosterDate || dayjs()).format('YYYY-MM-DD'));
              setUploading(true); setUploadProgress(0);
              api.post('/songs/bulk-posters', formData, uploadConfig).then(res => {
                setUploadProgress(100);
                const sc = (res.data.suggestions || []).length;
                message.success(`${res.data.matched.length} matched, ${res.data.unmatched.length} unmatched` + (sc ? `, ${sc} name suggestion(s)` : ''));
                setBulkPosterModal(false);
                setUploading(false);
                fetchSongs();
                fetchSuggestions();
              }).catch(() => { message.error('Upload failed'); setUploading(false); });
            }
          }}>Upload</Button>
        ]}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ marginRight: 8 }}>Upload Date:</Text>
          <DatePicker value={bulkPosterDate} onChange={(d) => setBulkPosterDate(d || dayjs())} allowClear={false} format="YYYY-MM-DD" />
        </div>
        <div id="bulk-poster-upload">
          <Upload multiple beforeUpload={() => false} accept="image/*" listType="picture">
            <Button icon={<UploadOutlined />}>Select Poster Images</Button>
          </Upload>
        </div>
        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
          Poster file names will be matched to song titles automatically. E.g., "My Song.jpg" matches song "My Song".
        </Text>
      </Modal>

      {/* Name Merge Suggestions Modal */}
      <Modal title="Name Suggestions (from poster names)" open={suggestModal} onCancel={() => setSuggestModal(false)} footer={[
        <Button key="close" onClick={() => setSuggestModal(false)}>Close</Button>
      ]} width={640}>
        <Text type="secondary">When a poster's name differs slightly from the audio, the system suggests the poster's full name. "Merge" updates the audio's title to that full name.</Text>
        {mergeGroups.length > 0 && (
          <div style={{ margin: '12px 0', padding: '10px 14px', background: '#e6f7ff', borderRadius: 6, border: '1px solid #91d5ff' }}>
            <Text strong style={{ color: '#1890ff' }}>{mergeGroups.length} groups of similar songs found!</Text>
            <Button type="link" size="small" onClick={() => { setSuggestModal(false); setMergeModalVisible(true); }}>
              Open Merge Suggestions →
            </Button>
          </div>
        )}
        {suggestions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>No pending poster name suggestions.</div>
        ) : (
          <List
            style={{ marginTop: 12 }}
            dataSource={suggestions}
            renderItem={(s) => (
              <List.Item
                actions={[
                  <Popconfirm key="merge" title={`Set title to "${s.suggested_title}"?`} okText="Merge" onConfirm={() => applySuggestion(s.id)}>
                    <Button type="primary" size="small" icon={<CheckCircleFilled />}>Merge</Button>
                  </Popconfirm>,
                  <Button key="ignore" size="small" onClick={() => ignoreSuggestion(s.id)}>Ignore</Button>
                ]}
              >
                <List.Item.Meta
                  avatar={s.poster_path ? <Image src={`${API_BASE}/${s.poster_path}`} width={48} height={48} style={{ borderRadius: 4, objectFit: 'cover' }} /> : null}
                  title={<span><Text delete type="secondary">{s.current_title}</Text> <Text type="secondary">→</Text> <Text strong>{s.suggested_title}</Text></span>}
                  description={s.company_name ? <Tag>{s.company_name}</Tag> : null}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Duplicate Songs Confirmation Modal */}
      <Modal title="Similar Songs Found" open={dupModal} onCancel={() => { setDupModal(false); setDupItems([]); setDupPendingUpload(null); }}
        width={650} footer={[
          <Button key="cancel" onClick={() => { setDupModal(false); setDupItems([]); setDupPendingUpload(null); }}>Cancel — Don't Upload</Button>,
          <Button key="upload" type="primary" onClick={() => handleDupConfirm(dupItems.map(d => d.new_title))}>
            Not Same — Upload All
          </Button>
        ]}>
        <Typography.Paragraph type="warning" style={{ marginBottom: 12 }}>
          These songs have similar titles to existing songs. Are they the same or different?
        </Typography.Paragraph>
        {dupItems.map((d, i) => (
          <Card key={i} size="small" style={{ marginBottom: 8, background: '#fffbe6', borderColor: '#ffe58f' }}>
            <div><Tag color="blue">New</Tag> <Text strong>{d.new_title}</Text> <Text type="secondary">({d.filename})</Text></div>
            <div style={{ marginTop: 4 }}>
              {d.existing.map((e, j) => (
                <div key={j}><Tag color="orange">Existing</Tag> <Text>{e.title}</Text> <Text type="secondary">(ID: {e.id})</Text></div>
              ))}
            </div>
          </Card>
        ))}
      </Modal>

      {/* Merge Suggestions Modal */}
      <Modal title={`Merge Suggestions (${mergeGroups.length} groups of similar songs)`} open={mergeModalVisible} onCancel={() => { setMergeModalVisible(false); setMergingGroup(null); }}
        width={750} footer={[<Button key="close" onClick={() => setMergeModalVisible(false)}>Close</Button>]}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Click on a song title to select it as the main title when merging. "Separate" marks songs as intentionally different.
        </Text>
        {mergeGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>No similar songs found. All titles are unique!</div>
        ) : (
          mergeGroups.map((group, gi) => (
            <Card key={gi} size="small" style={{ marginBottom: 12, borderColor: mergingGroup === gi ? '#1890ff' : '#d9d9d9', borderWidth: mergingGroup === gi ? 2 : 1 }}>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <Text strong style={{ fontSize: 15 }}>Group {gi + 1}: </Text>
                  <Text type="secondary">{group.length} similar songs</Text>
                  {group[1]?.match_pct && <Tag color="volcano" style={{ marginLeft: 8 }}>{group[1].match_pct}% match</Tag>}
                </span>
              </div>
              {group.map((s) => (
                <div key={s.id}
                  onClick={() => setMergingGroup(mergingGroup === `${gi}-${s.id}` ? null : `${gi}-${s.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, padding: '6px 10px',
                    background: mergingGroup === `${gi}-${s.id}` ? '#e6f7ff' : '#fafafa',
                    border: mergingGroup === `${gi}-${s.id}` ? '2px solid #1890ff' : '1px solid transparent',
                    borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s'
                  }}>
                  {mergingGroup === `${gi}-${s.id}` && <CheckCircleFilled style={{ color: '#1890ff', fontSize: 16 }} />}
                  <Text strong={mergingGroup === `${gi}-${s.id}`} style={{ flex: 1 }}>{s.title}</Text>
                  {s.audio_path ? <Tag color="green">Audio</Tag> : <Tag>No Audio</Tag>}
                  {s.poster_path ? <Tag color="blue">Poster</Tag> : <Tag>No Poster</Tag>}
                  {s.isrc ? <Tag color="purple">ISRC</Tag> : null}
                  {s.upc ? <Tag color="cyan">UPC</Tag> : null}
                  {s.singer ? <Tag color="orange">{s.singer}</Tag> : null}
                  <Tag>{s.distribution_status || 'pending'}</Tag>
                </div>
              ))}
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Popconfirm title="Mark these songs as separate? They won't appear in suggestions again."
                  onConfirm={() => dismissMergeGroup(group.map(s => s.id))}
                  okText="Yes, Separate" okButtonProps={{ danger: true }}>
                  <Button size="small">Separate (Not Same)</Button>
                </Popconfirm>
                {group.some((s, si) => mergingGroup === `${gi}-${s.id}`) ? (
                  <Button type="primary" size="small"
                    onClick={() => {
                      const selected = group.find((s) => mergingGroup === `${gi}-${s.id}`);
                      if (selected) mergeSongs(group.map(s => s.id), selected.title);
                    }}>
                    Merge → "{group.find((s) => mergingGroup === `${gi}-${s.id}`)?.title}"
                  </Button>
                ) : (
                  <Popconfirm title={`Merge ${group.length} songs into one? Click a title above to choose which name to keep, or click OK to auto-pick.`}
                    onConfirm={() => mergeSongs(group.map(s => s.id))}
                    okText="Merge (auto-pick title)" okButtonProps={{ type: 'primary' }}>
                    <Button type="primary" size="small">Merge All</Button>
                  </Popconfirm>
                )}
              </div>
            </Card>
          ))
        )}
      </Modal>
    </div>
  );
}
